/**
 * The review queue's derivations (LG-414): who owes the next move, how long
 * they have waited, and whether that wait has gone on too long.
 *
 * Pure — no I/O — so the tRPC router, the "N need action" badge, and the
 * staging seed script all answer identically. The router seam is where the
 * ordering and grouping are tested; this module is where the rules live.
 *
 * The problem this exists to solve: the queue used to be `createdAt DESC`,
 * which sinks the people who have waited longest to the bottom, and nothing
 * anywhere said that anyone was waiting at all.
 */

import {
  APPLIED_AT_OPTIONAL,
  APPLIED_AT_TERMINAL,
  type AppliedAtSource,
} from '../../db/schema/audition-applied-at.js'
import type {
  AuditionLane,
  CalendarBookingStatus,
  CallBookingStatus,
  CallProposalStatus,
} from '../../db/schema/enums.js'
import { AUDITION_STAGES, type AuditionStage } from './state-machine.js'

/** Days at the same stage, still owing an admin move, before it reads as overdue. */
export const OVERDUE_DAYS = 7

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * Which block of the queue a row belongs in. The split is by *who owes the
 * next move*, not by stage: an admin scanning the page should be able to
 * ignore everything outside the first group.
 */
export type QueueGroup =
  | 'needs_action'
  | 'waiting_on_applicant'
  | 'not_yet_submitted'
  | 'archived_accepted'
  | 'archived_rejected'

/**
 * The groups whose membership is decided by the stage ALONE (LG-914), in the
 * order an admin reads them.
 *
 * Two facts make this list the seam the queue is paginated on. They are the
 * only groups a SQL predicate can select without re-implementing
 * `deriveQueueRow` — every other group needs the proposals, the bookings and a
 * clock. And they are the three that grow without bound: `audition.start`
 * inserts a `wizard_in_progress` row the moment a visitor types an email into
 * the public form, and `client` / `rejected` rows are never pruned. The rest
 * of the queue is the applicants actually in flight, which is bounded by
 * cohort size the way `sortQueue`'s docstring always assumed the whole table
 * was.
 *
 * `deriveQueueRow` is the authority, not this table — the parity test asserts
 * they agree, in both directions, for every stage.
 */
export const ARCHIVE_GROUPS = [
  'not_yet_submitted',
  'archived_accepted',
  'archived_rejected',
] as const satisfies readonly QueueGroup[]

export type ArchiveGroup = (typeof ARCHIVE_GROUPS)[number]

/** The one stage each archive group is exactly the set of. */
export const ARCHIVE_GROUP_STAGE = {
  not_yet_submitted: 'wizard_in_progress',
  archived_accepted: 'client',
  archived_rejected: 'rejected',
} as const satisfies Record<ArchiveGroup, AuditionStage>

/**
 * How each archive is named where it stands on its own — the archive page's
 * group chips and the queue's archive strip — so "Accepted" does not need
 * "Archived —" in front of it to be understood.
 */
export const ARCHIVE_GROUP_LABELS = {
  not_yet_submitted: 'Not yet submitted',
  archived_accepted: 'Accepted',
  archived_rejected: 'Rejected',
} as const satisfies Record<ArchiveGroup, string>

/**
 * The archive a URL segment names, or null for one that names none.
 *
 * `find` rather than `includes` + assertion: the return type narrows to
 * `ArchiveGroup` without a cast, so a renamed group cannot leave a stale
 * string compiling here.
 */
export function toArchiveGroup(
  value: string | null | undefined,
): ArchiveGroup | null {
  return ARCHIVE_GROUPS.find((group) => group === value) ?? null
}

/**
 * Everything else: the stages an admin can still owe a move on, and the only
 * ones `needsAction` or `overdue` can ever be true for.
 *
 * Derived by subtraction rather than listed, so a stage added to the state
 * machine joins the bounded live read by default. That is the safe direction
 * to fail in — a new stage nobody thought about shows up in the queue and is
 * dealt with, instead of silently becoming invisible.
 */
// Annotated, not asserted, and hoisted out of the callback. `as
// AuditionStage[]` would widen the element type for `.includes` by switching
// the check OFF: a stage that is not in the union stops being a compile error
// and starts being a value that subtracts nothing, putting an archive stage
// back into the capped live read while `browse` also serves it. The annotation
// widens and checks; the `satisfies` on the table above is then the second
// guard rather than the only one.
const ARCHIVE_STAGES: readonly AuditionStage[] =
  Object.values(ARCHIVE_GROUP_STAGE)

export const LIVE_PIPELINE_STAGES: readonly AuditionStage[] =
  AUDITION_STAGES.filter((stage) => !ARCHIVE_STAGES.includes(stage))

/**
 * The call fact, orthogonal to the stage on purpose (LG-414). "Has had a
 * call" and "is awaiting one" are questions the stage cannot answer — a
 * `payment_unlocked` applicant may have had a call, been let in without one,
 * or no-showed one — so the queue derives it from the proposals instead of
 * inventing stages for every combination.
 *
 * Each bucket carries ONE fact (LG-1052). The two that look alike in the
 * proposal table are opposite facts to a reviewer: `none` is "no call has
 * entered the picture yet — still in progress", while the two `*_no_call`
 * facets are "we moved on without ever talking to them", split by which way
 * the decision went. Likewise `offered` (slots on the table, or a booking
 * still pending the eligibility gate) and `scheduled` (a slot locked in) were
 * once one bucket, and an admin could not tell "they have not picked" from "it
 * is on the calendar".
 */
export type CallFacet =
  | 'none'
  | 'offered'
  | 'scheduled'
  | 'held'
  | 'no_show'
  | 'accepted_no_call'
  | 'rejected_no_call'

/** The three decisions the admin surface offers (see the LG-414 product model). */
export type AuditionDecision = 'accept' | 'book_call' | 'reject'

/** Admin-facing stage words. Never user-facing — see CONTEXT.md. */
export const STAGE_LABELS: Record<AuditionStage, string> = {
  wizard_in_progress: 'Filling in the form',
  audition_completed: 'Applied',
  paid_pending_call: 'Paid, awaiting call',
  vibe_call_booked: 'Call booked',
  payment_unlocked: 'Payment unlocked',
  client: 'Client',
  rejected: 'Rejected',
}

/**
 * Admin-facing lane words. The db enum keeps its original names.
 *
 * `satisfies` rather than an annotation: `laneLabel` indexes this by
 * `keyof typeof`, which needs the literal keys, and the constraint still
 * makes a lane added to the enum an error HERE rather than at every reader.
 */
export const LANE_LABELS = {
  fast: 'Pay-first',
  consideration: 'Talk-first',
} as const satisfies Record<AuditionLane, string>

/**
 * The lanes, in the order an admin should read them — the counterpart to
 * `AUDITION_STAGES`, so a UI listing lanes never has to assert
 * `Object.keys()` back into the union.
 */
export const AUDITION_LANES = [
  'fast',
  'consideration',
] as const satisfies readonly AuditionLane[]

/**
 * The subset of a submission the queue reasons about.
 *
 * Extends the ladder's own shape rather than respelling its three timestamps:
 * that module promises a fourth rung is one edit, and a second hand-written
 * copy of the members here is the edit it would not have mentioned.
 */
export interface QueueSubmission extends AppliedAtSource {
  stage: AuditionStage
  hasPaid: boolean
}

/** The subset of a call proposal the queue reasons about. */
export interface QueueProposal {
  status: CallProposalStatus
  calendarStatus: CalendarBookingStatus
  selectedSlot: Date | null
}

/** The subset of a cal.com booking the queue reasons about (LG-902). */
export interface QueueBooking {
  status: CallBookingStatus
  startAt: Date
  hostNoShow: boolean
}

/**
 * cal.com's vocabulary in the funnel's own words, so the Call facet can reason
 * about both sources with one ladder.
 *
 * `pending` becomes `proposed` rather than `selected`: a request awaiting the
 * eligibility gate holds a slot but is not yet a call anyone agreed to, and
 * reading it as booked would tell an admin a call is on the calendar before
 * we have decided whether to allow it.
 */
const BOOKING_AS_PROPOSAL: Record<CallBookingStatus, CallProposalStatus> = {
  pending: 'proposed',
  booked: 'selected',
  declined: 'cancelled',
  cancelled: 'cancelled',
  held: 'held',
  no_show: 'no_show',
}

/**
 * A cal.com booking, seen as the call record the queue already reasons about.
 *
 * "Has this applicant had a call, and where did it get to" is one question,
 * and it must be answered the same way whether the call came from the in-app
 * picker or from cal.com — two precedence ladders would eventually disagree
 * about the same applicant. Only the vocabulary differs: cal.com's `booked`
 * is the proposal table's `selected` (a slot is locked in); `held`,
 * `no_show` and `cancelled` are already the same words, which is why the
 * enums were deliberately given the same ones.
 *
 * What this does NOT flatten is the action owed — an admin can retry a
 * calendar write or record an outcome on a proposal, and can do neither on a
 * cal.com booking. `owedFor` is told about bookings separately for that.
 */
export function bookingAsCall(booking: QueueBooking): QueueProposal {
  return {
    status: BOOKING_AS_PROPOSAL[booking.status],
    // cal.com owns the invite; there is no Google write of ours to retry, so
    // this must read as "nothing to repair" rather than as a failure.
    calendarStatus: 'none',
    selectedSlot: booking.startAt,
  }
}

export interface QueueDerivation {
  group: QueueGroup
  /** True only when an ADMIN owes the next move. Drives the home-card badge. */
  needsAction: boolean
  /** needsAction and waiting longer than {@link OVERDUE_DAYS}. */
  overdue: boolean
  /**
   * The instant {@link waitDays} and {@link overdue} were measured from.
   *
   * It rides on the derivation rather than being spliced on by each caller:
   * a surface that shows the age from here and the date from `createdAt`
   * tells a resubmitted applicant they have waited a day, under a tooltip
   * dated two months earlier.
   */
  appliedAt: Date
  /** Whole days since {@link appliedAt}. */
  waitDays: number
  /** One sentence naming the move that is owed, and by whom. */
  owed: string
  /** The decision the header CTA should offer, when one is obvious. */
  primaryDecision: AuditionDecision | null
  call: CallFacet
}

/**
 * The ladder itself lives with the columns it names — the table's paging
 * index is generated from the same list, and a schema module cannot import
 * this one without inverting the arrow. This is its TypeScript reader.
 */
export function appliedAt(submission: AppliedAtSource): Date {
  for (const field of APPLIED_AT_OPTIONAL) {
    const value = submission[field]
    if (value) return value
  }
  return submission[APPLIED_AT_TERMINAL]
}

/** Whole days waited, floored, never negative (a clock skew must not read as -1). */
export function waitDays(submission: QueueSubmission, now: Date): number {
  const elapsed = now.getTime() - appliedAt(submission).getTime()
  return Math.max(0, Math.floor(elapsed / DAY_MS))
}

/**
 * Stages only reachable by an admin decision, by which way it went. Arriving
 * here without a call in the history means the decision was made without one.
 */
const ACCEPTED_WITHOUT_A_CALL: readonly AuditionStage[] = [
  'payment_unlocked',
  'client',
]
const REJECTED_WITHOUT_A_CALL: readonly AuditionStage[] = ['rejected']

/**
 * Whether a stage is one of those — i.e. whether a `rejected_no_call` row
 * got there by a decision rather than by a withdrawn offer.
 *
 * Exported because the row's CHIP has to tell those two apart (`callLabel`)
 * out of the one bucket `callFacet` merges them into, and a second copy of
 * the list — or a hardcoded `stage !== 'rejected'` standing in for it —
 * would go on compiling, and start lying, the day a stage joins this one.
 */
export function isRejectedWithoutACallStage(stage: AuditionStage): boolean {
  return REJECTED_WITHOUT_A_CALL.includes(stage)
}

/**
 * The call fact for one applicant, from their whole proposal history and where
 * that history left them.
 *
 * Deliberately NOT `proposals[0]`: order is not meaning here. A held call
 * outranks everything (it is the fact a decision is made on), then a slot
 * locked in, then an offer still open, then a no-show.
 *
 * Below those, no call happened and none is coming, and the STAGE says why.
 * The proposal table cannot tell "never offered a call" from "let in without
 * one" from "refused without one" — all three can be an empty list — and
 * conflating them is how a "skipped the call" filter ends up returning
 * everybody who applied yesterday, and how someone rejected with no call ever
 * offered read as "No call yet", i.e. still waiting on us (LG-1052). A
 * decision outranks a withdrawn offer: the withdrawal is history, the decision
 * is the fact. A withdrawn offer with no decision yet reads as refused,
 * because that is what withdrawing one means — though the row's chip says
 * "Offer withdrawn" until the decision exists (`callLabel`).
 */
export function callFacet(
  submission: Pick<QueueSubmission, 'stage'>,
  proposals: readonly QueueProposal[],
): CallFacet {
  if (proposals.some((p) => p.status === 'held')) return 'held'
  if (proposals.some((p) => p.status === 'selected')) return 'scheduled'
  if (proposals.some((p) => p.status === 'proposed')) return 'offered'
  if (proposals.some((p) => p.status === 'no_show')) return 'no_show'
  if (ACCEPTED_WITHOUT_A_CALL.includes(submission.stage))
    return 'accepted_no_call'
  if (
    isRejectedWithoutACallStage(submission.stage) ||
    proposals.some((p) => p.status === 'cancelled')
  )
    return 'rejected_no_call'
  return 'none'
}

/** True while a proposal's booked slot is still in the future. */
function isUpcoming(proposal: QueueProposal, now: Date): boolean {
  return (
    proposal.status === 'selected' &&
    !!proposal.selectedSlot &&
    proposal.selectedSlot.getTime() > now.getTime()
  )
}

/**
 * A calendar write that failed AND can still be repaired.
 *
 * The `selected` half is not decoration: `retryCalendarBooking` claims the
 * row on `status = 'selected'`, so once the call is marked held or a no-show
 * the retry answers PRECONDITION_FAILED forever. Keying the owed-work flag
 * on `calendarStatus` alone pinned such a row to "retry the booking" for the
 * rest of its life — masking the decision that was actually owed, behind a
 * button that could never succeed.
 */
export function isRetryableBooking(proposal: QueueProposal): boolean {
  return proposal.calendarStatus === 'failed' && proposal.status === 'selected'
}

/**
 * What the applicant's stage and call history say is owed next.
 *
 * The stage alone is not enough at `vibe_call_booked`: the same stage covers
 * "the call is tomorrow" (nothing owed), "the call was yesterday and nobody
 * recorded it" (owed), and "they never showed" (owed) — and treating all
 * three as waiting is how someone sits unseen after their call.
 */
function owedFor(
  submission: QueueSubmission,
  proposals: readonly QueueProposal[],
  /** cal.com bookings, already projected by {@link bookingAsCall}. */
  bookings: readonly QueueProposal[],
  now: Date,
): Pick<QueueDerivation, 'group' | 'needsAction' | 'owed' | 'primaryDecision'> {
  // Both sources answer "where did this call get to"; only the owed ACTION
  // below distinguishes them.
  const calls = [...proposals, ...bookings]
  const waiting = (owed: string) => ({
    group: 'waiting_on_applicant' as const,
    needsAction: false,
    owed,
    primaryDecision: null,
  })
  const act = (owed: string, primaryDecision: AuditionDecision | null) => ({
    group: 'needs_action' as const,
    needsAction: true,
    owed,
    primaryDecision,
  })

  switch (submission.stage) {
    case 'wizard_in_progress':
      return {
        group: 'not_yet_submitted',
        needsAction: false,
        owed: 'Still filling in the form — not an applicant yet',
        primaryDecision: null,
      }
    case 'client':
      return {
        group: 'archived_accepted',
        needsAction: false,
        owed: 'In the cohort',
        primaryDecision: null,
      }
    case 'rejected':
      return {
        group: 'archived_rejected',
        needsAction: false,
        owed: 'Rejected — in cooldown',
        primaryDecision: null,
      }
    case 'audition_completed':
      return act('Decide — accept, book a call, or reject', 'accept')
    case 'paid_pending_call':
      // Highest urgency in the funnel: they have handed over money and are
      // waiting on us.
      return act('Already paid — accept, book a call, or reject', 'accept')
    case 'payment_unlocked':
      return waiting('Payment unlocked — waiting on them to pay')
    case 'vibe_call_booked': {
      if (calls.some((p) => p.status === 'held'))
        return act('Call held — accept or reject', 'accept')
      if (calls.some((p) => isUpcoming(p, now)))
        return waiting('Call booked — on the calendar')
      if (proposals.some((p) => p.status === 'selected'))
        // Booked, the slot has passed, and nobody said whether it happened.
        // Without this the applicant waits forever behind a call we have
        // already had.
        return act('Call time has passed — record held or no-show', null)
      if (bookings.some((p) => p.status === 'selected'))
        // Same gap, different remedy: cal.com owns a booking's outcome, and
        // nothing in this app can record it. Naming the in-app action here
        // would send an admin hunting for a button that does not exist.
        return act('Call time has passed — mark the outcome in cal.com', null)
      if (proposals.some((p) => p.status === 'proposed'))
        return waiting('Times offered — waiting on them to pick one')
      return act('No-show — decide, or invite them to book again', 'accept')
    }
    default:
      // The stage comes out of the database, so the compile-time
      // exhaustiveness above is only half the guarantee. Falling through
      // silently returned `undefined`, which spread into a row with no
      // `group` — matching no heading, so the applicant vanished from the
      // page entirely. That is the exact outcome this queue exists to stop.
      return assertNeverStage(submission.stage)
  }
}

function assertNeverStage(stage: never): never {
  throw new Error(`Unhandled audition stage: ${String(stage)}`)
}

/**
 * Everything the queue and the detail header need to know about one row.
 *
 * `proposals` must already be soft-delete filtered by the caller — this
 * module never reads the database.
 */
export function deriveQueueRow({
  submission,
  proposals,
  bookings = [],
  now,
}: {
  submission: QueueSubmission
  proposals: readonly QueueProposal[]
  /** cal.com bookings for this applicant (LG-902). */
  bookings?: readonly QueueBooking[]
  now: Date
}): QueueDerivation {
  const bookingCalls = bookings.map(bookingAsCall)
  const base = owedFor(submission, proposals, bookingCalls, now)
  const days = waitDays(submission, now)

  // A calendar write that exhausted its retries leaves a confirmed call with
  // no invite on anybody's calendar. It is admin work regardless of stage,
  // and it outranks the stage's own answer because the applicant thinks they
  // have a meeting and does not.
  const bookingFailed =
    base.group !== 'archived_accepted' &&
    base.group !== 'archived_rejected' &&
    base.group !== 'not_yet_submitted' &&
    proposals.some(isRetryableBooking)

  const derived = bookingFailed
    ? {
        group: 'needs_action' as const,
        needsAction: true,
        owed: 'Calendar invite never went out — retry the booking',
        primaryDecision: base.primaryDecision,
      }
    : base

  return {
    ...derived,
    appliedAt: appliedAt(submission),
    waitDays: days,
    overdue: derived.needsAction && days > OVERDUE_DAYS,
    call: callFacet(submission, [...proposals, ...bookingCalls]),
  }
}

/**
 * Which decisions are legal for a stage, so the UI can disable rather than
 * offer a button the server will refuse.
 *
 * This mirrors the state machine and the `approve` PaymentIntent guard; it is
 * a convenience for rendering, never the enforcement. The server refuses
 * independently.
 */
export function availableDecisions(submission: {
  stage: AuditionStage
  hasPaid: boolean
}): Record<AuditionDecision, boolean> {
  const decidable =
    submission.stage === 'audition_completed' ||
    submission.stage === 'paid_pending_call' ||
    submission.stage === 'vibe_call_booked' ||
    submission.stage === 'payment_unlocked'

  return {
    // Accepting an already-unlocked applicant is a no-op that the state
    // machine refuses — the acceptance already happened, and only paying
    // makes them a client.
    accept: decidable && submission.stage !== 'payment_unlocked',
    // Legal right through `payment_unlocked`: an unlocked applicant who wants
    // to talk before paying still gets a scheduling link, and the stage's
    // self-loop is what keeps that booking from being refused.
    book_call: decidable,
    reject: decidable,
  }
}

/**
 * "1 needs action" / "4 need action" — shared so the home card and the queue
 * cannot disagree about the verb, the way they already cannot disagree about
 * the number.
 */
export function formatNeedsAction(count: number): string {
  return `${count} ${count === 1 ? 'needs' : 'need'} action`
}

/** Human wait age for a row: "applied today" / "waiting 9 days". */
export function formatWait(days: number): string {
  if (days === 0) return 'applied today'
  if (days === 1) return 'waiting 1 day'
  return `waiting ${days} days`
}

export type QueueSort = 'longest_waiting' | 'newest'

/**
 * The queue's order.
 *
 * `longest_waiting` — the default, and the whole point of LG-414 — puts the
 * oldest application first. The old `createdAt DESC` did the exact opposite:
 * it sank whoever had waited longest to the bottom of a page nobody scrolls,
 * which is how an applicant sits unseen indefinitely.
 *
 * Deliberately NOT blended with urgency. "Paid" and "overdue" are louder
 * signals than position — they are a chip, a colour and a filter — and a
 * control labelled "Longest waiting" that silently re-ranks by something else
 * is a control an admin stops trusting.
 *
 * Sorted in the router rather than in SQL, because one clock in one place
 * cannot disagree with itself the way a `COALESCE` in the ORDER BY and an
 * `appliedAt()` in the labels eventually would.
 *
 * That only holds over a bounded set, and the set this originally assumed —
 * cohort size, invite ~30 take 10 — was never the table: a `wizard_in_progress`
 * row is written the moment a stranger types an email. So this now sorts the
 * LIVE_PIPELINE_STAGES read only. The archives are read a page at a time and
 * arrive already ordered by the same expression in SQL; re-sorting a page here
 * would be a second opinion about where the page boundary was.
 *
 * The `id` tie-breaker is not decoration. `submittedAt` is only as granular as
 * the clock that wrote it, and a seeded or bulk-imported batch shares one to
 * the millisecond — without it the page reshuffles between refreshes and
 * cannot be reviewed top-down.
 */
export function sortQueue<T extends { id: string; appliedAt: Date }>(
  rows: readonly T[],
  sort: QueueSort,
): T[] {
  const direction = sort === 'newest' ? -1 : 1
  return [...rows].sort(
    (a, b) =>
      direction * (a.appliedAt.getTime() - b.appliedAt.getTime()) ||
      direction * a.id.localeCompare(b.id),
  )
}
